import React from 'react';
import {
  Edit,
  Show,
  Responsive,
  Datagrid,
  Filter,
  CreateButton,
  ShowButton,
  EditButton,
  SaveButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  ReferenceField,
  DateField,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  DateInput,
  FormDataConsumer,
  Toolbar,
  FunctionField,
  required, WithPermissions,
} from 'react-admin';
import Typography from '@material-ui/core/Typography';
import { CreateBody } from '../components/CreateBody';
import { List } from '../components/List';
import { withStyles } from '@material-ui/core/styles';
import HiddenInput from '../components/HiddenInput';
import { NormalActions } from '../components/NormalActions';
import { CustomPagination } from '../components/CustomPagination';
import RedirectToHome from '../components/RedirectToHome';
import MainListWrapper from '../components/MainListWrapper';

const styles = {
  filters: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: '1',
  },
};

export const AccountsList = (props) => (
  <MainListWrapper>
    <List {...props}
          filters={<AccountsFilter />}
          actions={<CustomNormalActions/>}
          sort={{ field: 'id', order: 'DESC' }}
          pagination={<CustomPagination />}
    >
      <Responsive
        medium={
          <Datagrid>
            <FunctionField source="id" render={record => record.id.replace('/accounts/', '')} />
            <TextField source="name" />

            <TextField source="type" />

            <DateField label="Trial" source="trialOrActivePeriodEnds" options={{ allowEmpty: true }} />

            <ReferenceField label="Agency" source="parent" reference="accounts" allowEmpty>
              <TextField source="name" />
            </ReferenceField>

            <TextField source="paymentMethod"/>
            <TextField source="status"/>

            <ShowButton />
            <EditButton className="edit-account-button"/>
          </Datagrid>
        }
      />
    </List>
  </MainListWrapper>
);

const accountTypeChoices = [
  { id: 'client', name: 'Client' },
  { id: 'agency', name: 'Agency' },
  { id: 'admin', name: 'Admin' },
];

const statusChoices = [
  { id: 'trial', name: 'Trial' },
  { id: 'active', name: 'Active' },
  { id: 'inactive', name: 'Inactive' },
  { id: 'waiting_for_payment', name: 'Waiting for payment' },
  { id: 'expiring', name: 'Expiring' },
];

const paymentChoicesForFilter = [
  { id: 'stripe', name: 'Stripe' },
  { id: 'individual', name: 'Individual'},
  { id: 'external', name: 'External'},
  { id: 'manual_stripe', name: 'manual_stripe'},
  { id: '@null@', name: ' - not defined -'}
];

const complianceDashboardVersionChoices = [
  { id: '/compliance_dashboard_access_levels/1', name: 'Basic' },
  { id: '/compliance_dashboard_access_levels/2', name: 'Advanced' },
];

const AccountsTitle = ({ record }) => {
  return <span>Account {record ? `"${record.name}"` : ''}</span>;
};

export const AccountsShow = (props) => (
  <Show title={<AccountsTitle />} {...props}>
    <SimpleShowLayout>
      <FunctionField source="id" render={record => record.id.replace('/accounts/', '')} />
      <TextField source="name" />

      <TextField source="type" />

      <DateField label="Trial Period Ends" source="trialOrActivePeriodEnds" allowEmpty />

      <TextField source="paymentMethod"/>

      <ReferenceField label="Agency" source="parent" reference="accounts" link="show" allowEmpty>
        <TextField source="name" allowEmpty />
      </ReferenceField>
    </SimpleShowLayout>
  </Show>
);

export const AccountsEdit = (props) => (
  <Edit title={<AccountsTitle />} {...props}>
    <SimpleForm toolbar={<CustomToolbarEdit/>}>
      <ReferenceField label="Parent agency" source="parent" reference="accounts" allowEmpty>
        <TextField source="name" />
      </ReferenceField>
      <FunctionField source="id" render={record => record.id.replace('/accounts/', '')} disabled />
      <TextInput source="name" validate={required()} />

      <SelectInput label="Type" source="type" choices={accountTypeChoices} validate={required()} />

      <TextInput label="Payment Method" source="paymentMethod" disabled />
      <SelectInput label="Status" source="status" choices={statusChoices} validate={required()} />

      <FormDataConsumer>
        {({
            formData,
            ...rest
          }) =>
          formData && formData.paymentMethod === 'individual' ? (
            <SelectInput label="Compliance dashboard version" source="complianceDashboardAccessLevel"
                         choices={complianceDashboardVersionChoices} validate={required()} {...rest} />
          ) : null
        }
      </FormDataConsumer>
      <FormDataConsumer>
        {({
            formData,
            ...rest
          }) =>
          formData.stripeCustomerId !== null && formData.stripeSubscriptionId !== null ? (
            <DateInput label="Trial Period Ends" source="trialOrActivePeriodEnds" {...rest} disabled />
          ) : <DateInput label="Trial Period Ends" source="trialOrActivePeriodEnds" {...rest} />
        }
      </FormDataConsumer>
    </SimpleForm>
  </Edit>
);

export const AccountsCreate = (props) => (
  <CreateBody {...props}>
    <SimpleForm toolbar={<CustomToolbarCreate />}>
      <ReferenceInput label="Parent agency" source="parent" reference="accounts"
        filterToQuery={searchText => ({ name: searchText })}
        filter={{ type: 'agency' }} >
        <AutocompleteInput optionText="name" allowEmpty />
      </ReferenceInput>

      <TextInput source="name" validate={required()} />

      <SelectInput label="Type" source="type" choices={accountTypeChoices} validate={required()} />

      <DateInput label="Trial Period Ends" source="trialOrActivePeriodEnds" />
      <HiddenInput source="complianceDashboardAccessLevel" initialValue='/compliance_dashboard_access_levels/1' />
      <HiddenInput source="paymentMethod" initialValue="individual" />
    </SimpleForm>
  </CreateBody>
);

const CustomNormalActions = (props) => (
  <NormalActions customClassName="create-account-button" {...props}>
    <CreateButton basePath={props.basePath}/>
  </NormalActions>
);

const CustomToolbarCreate= props => (
  <Toolbar {...props} >
    <SaveButton className="account-create-save-button"/>
  </Toolbar>
);

const CustomToolbarEdit= props => (
  <Toolbar {...props} >
    <SaveButton className="account-edit-save-button"/>
  </Toolbar>
);

const AccountsFilter = withStyles(styles)(({classes, ...props}) => (
  <div className={classes.filters}>
    {props.context === 'form' ?
    <Typography variant="h5">
      Accounts List
    </Typography> : null}
    <Filter {...props}>
      <TextInput label="Name" source="name" alwaysOn/>

      <ReferenceInput label="Agency" source="parent" reference="accounts"
                      filterToQuery={searchText => ({name: searchText})}
                      filter={{type: 'agency'}}>
        <AutocompleteInput optionText="name" />
      </ReferenceInput>

      <SelectInput label="Type" source="type" choices={accountTypeChoices}/>

      <SelectInput label="Payment Method" source="paymentMethod" choices={paymentChoicesForFilter}/>

    </Filter>
  </div>
));

const ManageAccessForAdmin = (WrappedComponent) => (props) => (<WithPermissions render={({ permissions = '' })=>{
  const hasAccess = permissions.includes('ROLE_ADMIN');
  return hasAccess ? <WrappedComponent {...props}/> : <RedirectToHome/>
}}/>)

export default {
  list: ManageAccessForAdmin(AccountsList),
  show: ManageAccessForAdmin(AccountsShow),
  edit: ManageAccessForAdmin(AccountsEdit),
  create: ManageAccessForAdmin(AccountsCreate)
};
